import React, { useEffect, useState } from 'react'

import cn from 'classnames'
import Skeleton from 'react-loading-skeleton'

import { apiGetPointsBreakdown } from '@src/api/point.api'
import { apiGenerateReferralCode } from '@src/api/point.api'
import { apiGetLeaderboard } from '@src/api/point.api'
import { getSandDollarsBalance } from '@src/api/sandDollars.api'
import CopyToClipboard from '@src/components/CopyToClipboard'
import LoadingIcon from '@src/components/icons/Loading'
import PointIcon from '@src/components/icons/Point'
import Reload from '@src/components/icons/Reload'
import SandDollarIcon from '@src/components/icons/SandDollar'
import { MIN_MILES_FOR_REFERRAL_CODE } from '@src/constants/miles'
import { useFetchGeneratedReferralCode } from '@src/hooks/useFetchGeneratedReferralCode'
import { useWalletConnections } from '@src/hooks/useWalletConnections'
import RefreshButton from '@src/pages/Exchange/components/Refresh'
import { Leaderboard } from '@src/pages/Rewards/Leaderboard'
import { Rules } from '@src/pages/Rewards/Rules'
import { RewardsConnectWallet } from '@src/pages/Rewards/components/ConnectWallet'
import RedeemModal from '@src/pages/Rewards/components/RedeemModal'
import { ILeaderboardDataResponse } from '@src/types/point.api'
import { isFeatureEnabled } from '@src/utils'
import { Balance } from '@src/utils/balance'
import { logger } from '@src/utils/logger'
import { shouldDisplayReferralCode } from '@src/utils/miles'
import { createSandDollarsBalance } from '@src/utils/sandDollars'
import { truncateAddress } from '@src/utils/truncateAddress'
import { useApplicationStore } from '@src/zustand_stores'

type Props = {}

const TAB_NAMES = {
    CHECK_REWARDS: 'Check rewards',
    RULES: 'Rules',
    LEADERBOARD: 'Leaderboard',
}

const tabs = [TAB_NAMES.CHECK_REWARDS, TAB_NAMES.RULES, TAB_NAMES.LEADERBOARD]

const PointsPage: React.FC<Props> = () => {
    const { evmWalletAddress } = useWalletConnections()
    const [selectedTab, setSelectedTab] = useState<string>(tabs[0])

    // Referral code logic
    const generatedReferralCode = useApplicationStore(state => state.generatedReferralCode)
    const { refetchGeneratedReferralCode } = useFetchGeneratedReferralCode()
    const [isGenerating, setIsGenerating] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Miles and Sand Dollars state
    const [miles, setMiles] = useState<number>(0)
    const [sandDollarsBalance, setSandDollarsBalance] = useState<Balance | null>(null)
    const [milesLoading, setMilesLoading] = useState(false)
    const [sandDollarsLoading, setSandDollarsLoading] = useState(false)

    // Leaderboard state
    const [leaderboard, setLeaderboard] = useState<ILeaderboardDataResponse[]>([])
    const [leaderboardLoading, setLeaderboardLoading] = useState(false)

    // Redeem modal state
    const [isRedeemModalOpen, setIsRedeemModalOpen] = useState(false)

    // Fetch miles data
    const fetchMiles = async () => {
        if (!evmWalletAddress) {
            setMiles(0)
            return
        }

        setMilesLoading(true)
        try {
            const breakdownData = await apiGetPointsBreakdown(evmWalletAddress)
            const pointsTotal = Object.values(breakdownData)
                .filter(value => typeof value === 'number')
                .reduce((sum, value) => sum + value, 0)
            setMiles(pointsTotal)
        } catch (error) {
            logger.error('Error fetching miles:', error)
            setMiles(0)
        } finally {
            setMilesLoading(false)
        }
    }

    // Fetch sand dollars data
    const fetchSandDollars = async () => {
        if (!evmWalletAddress) {
            setSandDollarsBalance(null)
            return
        }

        setSandDollarsLoading(true)
        try {
            const response = await getSandDollarsBalance(evmWalletAddress as `0x${string}`)
            const balance = createSandDollarsBalance(response)
            setSandDollarsBalance(balance)
        } catch (error) {
            logger.error('Error fetching sand dollars:', error)
            setSandDollarsBalance(null)
        } finally {
            setSandDollarsLoading(false)
        }
    }

    // Fetch leaderboard data
    const fetchLeaderboard = async () => {
        setLeaderboardLoading(true)
        try {
            const leaderboardData = await apiGetLeaderboard()
            setLeaderboard(leaderboardData)
        } catch (error) {
            logger.error('Error fetching leaderboard:', error)
            setLeaderboard([])
        } finally {
            setLeaderboardLoading(false)
        }
    }

    // Handle referral code generation
    const handleGenerateReferralCode = async () => {
        if (!evmWalletAddress) return

        setIsGenerating(true)
        setError(null)
        try {
            const result = await apiGenerateReferralCode(evmWalletAddress)
            if (result.status !== 'success') throw new Error(result.message)
            refetchGeneratedReferralCode()
        } catch (error) {
            console.error('Error generating referral code', error)
            setError(error instanceof Error ? error.message : 'Something went wrong. Please try again later')
        } finally {
            setIsGenerating(false)
        }
    }

    // Render referral code logic
    const renderReferralCode = () => {
        if (!evmWalletAddress) return null

        if (shouldDisplayReferralCode(miles)) {
            // miles >= 10k and referral code exists
            if (
                generatedReferralCode &&
                generatedReferralCode.code !== '' &&
                generatedReferralCode.userAddress.toLowerCase() === evmWalletAddress.toLowerCase()
            ) {
                return (
                    <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium !leading-5 text-white'>{generatedReferralCode.code}</span>
                        <div className='flex h-6 w-6 items-center justify-center'>
                            <CopyToClipboard
                                textToCopy={generatedReferralCode.code}
                                textType='Referral Code'
                                anchorId='referral-code'
                                fitContent={false}
                            />
                        </div>
                    </div>
                )
            }
            // miles >= 10k and referral code does not exist. need to generate one
            else {
                return (
                    <button
                        onClick={handleGenerateReferralCode}
                        disabled={isGenerating}
                        className='flex items-center gap-1.5 rounded-md bg-[#29A74D] px-2 py-1 text-sm font-medium text-white transition-colors hover:bg-[#238A42] disabled:opacity-50'
                    >
                        {isGenerating ? (
                            <>
                                Generating <LoadingIcon className='h-4 w-4 animate-spin' />
                            </>
                        ) : (
                            'Generate'
                        )}
                    </button>
                )
            }
        }

        // miles < 10k and referral code does not exist
        return (
            <span className='text-sm font-medium !leading-6 text-[red]'>
                Available after {MIN_MILES_FOR_REFERRAL_CODE.toLocaleString()} miles
            </span>
        )
    }

    // Fetch both miles and sand dollars when wallet address changes
    useEffect(() => {
        if (evmWalletAddress) {
            fetchMiles()
            fetchSandDollars()
        } else {
            setMiles(0)
            setSandDollarsBalance(null)
        }
    }, [evmWalletAddress])

    // Fetch leaderboard when component mounts or when switching to leaderboard tab
    useEffect(() => {
        if (selectedTab === TAB_NAMES.LEADERBOARD) {
            fetchLeaderboard()
        }
    }, [selectedTab])

    // Handle redeem button click
    const handleRedeem = () => {
        // Only open modal if there's a valid balance and it's greater than 0
        if (sandDollarsBalance && sandDollarsBalance.bigintValue > BigInt(0)) {
            setIsRedeemModalOpen(true)
        }
    }

    // If no wallet connected, show connect wallet component
    if (!evmWalletAddress) {
        return <RewardsConnectWallet />
    }

    // Main rewards content when wallet is connected
    const renderCheckRewards = () => {
        // Check if Sand Dollars balance is below minimum required (5)
        const sandDollarsAmount = sandDollarsBalance ? parseFloat(sandDollarsBalance.formatWithMagnitudes(6)) : 0
        const isDisabled = sandDollarsAmount < 5
        const usdcEquivalent = sandDollarsAmount.toFixed(6) // SD is 1:1 with USDC

        return (
            <div className='flex flex-col gap-3'>
                {/* Miles Card */}
                <div className='flex flex-col gap-2 rounded-xl bg-[#212326] p-4'>
                    <div className='flex items-center gap-2'>
                        <PointIcon height='24' width='24' />
                        <span className='text-base font-medium leading-6 text-white'>Your Miles</span>
                    </div>
                    <span className='text-[30px] font-medium leading-9 text-white'>
                        {milesLoading ? (
                            <Skeleton enableAnimation baseColor='#9B9B9B' className='h-9 w-24' />
                        ) : (
                            miles.toLocaleString('en-US')
                        )}
                    </span>
                </div>

                {/* Sand Dollars Card */}
                {isFeatureEnabled('SAND_DOLLARS') && (
                    <div className='flex flex-col gap-4 rounded-xl bg-[#212326] p-4'>
                        <div className='flex flex-col gap-2'>
                            <div className='flex h-6 items-center gap-2'>
                                <SandDollarIcon height='24' width='24' />
                                <span className='text-base font-medium leading-6 text-white'>Your Sand Dollars</span>
                            </div>
                            <div className='flex items-center gap-2.5'>
                                <div className='flex flex-1 flex-col items-center gap-1'>
                                    <span className='w-full text-[30px] font-medium leading-9 text-white'>
                                        {sandDollarsLoading ? (
                                            <Skeleton enableAnimation baseColor='#9B9B9B' className='h-9 w-24' />
                                        ) : sandDollarsBalance ? (
                                            sandDollarsBalance.formatWithMagnitudes(4)
                                        ) : (
                                            '0.0000'
                                        )}
                                    </span>
                                    {!sandDollarsLoading && sandDollarsBalance && (
                                        <span className='w-full text-[10px] font-medium leading-3 text-[#757575]'>
                                            Worth {usdcEquivalent} USDC on Arbitrum
                                        </span>
                                    )}
                                </div>
                                <button
                                    onClick={isDisabled ? undefined : handleRedeem}
                                    disabled={isDisabled}
                                    className={`flex items-center gap-1.5 rounded-md p-1.5 transition-colors ${
                                        isDisabled
                                            ? 'cursor-not-allowed bg-[#161719]'
                                            : 'bg-[#FF6200] hover:bg-[#E55A00]'
                                    }`}
                                >
                                    <Reload className='h-4 w-4' />
                                    <span
                                        className={`text-center text-xs font-medium leading-4 ${
                                            isDisabled ? 'text-[#757575]' : 'text-white'
                                        }`}
                                    >
                                        Redeem
                                    </span>
                                </button>
                            </div>
                        </div>
                        {isDisabled && (
                            <div className='flex items-center justify-center self-stretch rounded-md bg-[#161719] p-2'>
                                <span className='text-center text-xs font-medium leading-4 text-white'>
                                    Minimum required to redeem: 5 Sand Dollars
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* Wallet and Referral Info */}
                <div className='flex flex-col gap-2'>
                    <div>
                        <span className='text-sm font-medium !leading-5 text-[#757575]'>Connected Wallet: </span>
                        <span className='text-sm font-medium !leading-5 text-white'>
                            {truncateAddress(evmWalletAddress, {
                                evmPrefixLen: 6,
                                evmSuffixLen: 4,
                                solanaPrefixLen: 6,
                                solanaSuffixLen: 4,
                                tronPrefixLen: 6,
                                tronSuffixLen: 4,
                            })}
                        </span>
                    </div>
                    <div className='mt-1'>
                        <div className='flex items-center gap-2'>
                            <span className='text-sm font-medium !leading-5 text-[#757575]'>Your referral code:</span>
                            {renderReferralCode()}
                        </div>
                        {error && <div className='mt-1 text-sm text-[red]'>{error}</div>}
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className='mx-4 flex h-full w-[334px] max-w-[calc(100vw-32px)] flex-col gap-6 rounded-xl bg-[#111112] p-4 sm:mx-0 sm:w-[432px] sm:max-w-none'>
            {/* Navigation Tabs */}
            <div className='flex items-center gap-3'>
                {tabs.map(tab => (
                    <button
                        key={tab}
                        onClick={() => setSelectedTab(tab)}
                        className={cn(
                            'flex items-center gap-1.5 rounded-lg p-2',
                            selectedTab === tab ? 'bg-white/10' : 'hover:bg-white/5'
                        )}
                    >
                        <span className='text-sm font-medium leading-5 text-white'>{tab}</span>
                    </button>
                ))}
            </div>

            {/* Tab Content */}
            {selectedTab === TAB_NAMES.CHECK_REWARDS && renderCheckRewards()}
            {selectedTab === TAB_NAMES.RULES && <Rules />}
            {selectedTab === TAB_NAMES.LEADERBOARD && (
                <Leaderboard account={evmWalletAddress} loading={leaderboardLoading} leaderboard={leaderboard} />
            )}

            {/* Redeem Modal */}
            {isRedeemModalOpen && sandDollarsBalance && (
                <RedeemModal
                    isOpen={isRedeemModalOpen}
                    onClose={() => setIsRedeemModalOpen(false)}
                    address={evmWalletAddress as `0x${string}`}
                    sandDollarsBalance={sandDollarsBalance}
                    onRedeemSuccess={fetchSandDollars}
                />
            )}
        </div>
    )
}

export default PointsPage
